import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle, ArrowRight } from "lucide-react";
import { api } from "@/lib/api";

export function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("checking");
  const attempts = useRef(0);

  useEffect(() => {
    if (!sessionId) { setStatus("error"); return; }
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") { setStatus("paid"); return; }
      } catch { /* keep polling */ }
      attempts.current += 1;
      if (attempts.current < 15) setTimeout(poll, 2000);
      else setStatus("pending");
    };
    poll();
  }, [sessionId]);

  return (
    <div className="flex min-h-[70vh] items-center justify-center px-4" data-testid="payment-success-page">
      <div className="glass max-w-md rounded-xl p-10 text-center">
        {status === "checking" && (
          <>
            <Loader2 className="mx-auto mb-4 h-12 w-12 animate-spin text-neon-cyan" />
            <h1 className="font-display text-2xl font-extrabold uppercase text-white">Confirming Payment</h1>
            <p className="mt-2 text-sm text-slate-400">Verifying with Stripe...</p>
          </>
        )}
        {status === "paid" && (
          <>
            <CheckCircle2 className="mx-auto mb-4 h-12 w-12 text-neon-green" />
            <h1 className="font-display text-2xl font-extrabold uppercase text-white" data-testid="payment-confirmed-heading">Payment Confirmed</h1>
            <p className="mt-2 text-sm text-slate-300">You're locked in. Show your confirmation email at the front desk — your receipt was sent by Stripe.</p>
            <p className="mt-3 select-all font-mono text-xs text-slate-500" data-testid="payment-session-id">{sessionId}</p>
            <Link to="/lounge" data-testid="payment-success-continue-button"
              className="mt-6 inline-flex items-center gap-2 rounded-md bg-neon-cyan px-6 py-3 font-display text-xs font-extrabold uppercase text-ink transition-colors hover:bg-white">
              Plan Your Visit <ArrowRight className="h-4 w-4" />
            </Link>
          </>
        )}
        {status === "pending" && (
          <>
            <Loader2 className="mx-auto mb-4 h-12 w-12 text-neon-gold" />
            <h1 className="font-display text-2xl font-extrabold uppercase text-white">Processing</h1>
            <p className="mt-2 text-sm text-slate-400">Payment is still processing. It will confirm shortly — check your email for the Stripe receipt.</p>
          </>
        )}
        {status === "error" && (
          <>
            <XCircle className="mx-auto mb-4 h-12 w-12 text-neon-magenta" />
            <h1 className="font-display text-2xl font-extrabold uppercase text-white">Missing Session</h1>
            <p className="mt-2 text-sm text-slate-400">We couldn't find that checkout session. Call us at 860-234-7909 and we'll sort it out.</p>
          </>
        )}
      </div>
    </div>
  );
}

export function PaymentCancel() {
  return (
    <div className="flex min-h-[70vh] items-center justify-center px-4" data-testid="payment-cancel-page">
      <div className="glass max-w-md rounded-xl p-10 text-center">
        <XCircle className="mx-auto mb-4 h-12 w-12 text-neon-magenta" />
        <h1 className="font-display text-2xl font-extrabold uppercase text-white">Checkout Canceled</h1>
        <p className="mt-2 text-sm text-slate-400">No charge was made. Your pass or deposit is waiting whenever you're ready.</p>
        <Link to="/" data-testid="payment-cancel-home-button"
          className="mt-6 inline-flex items-center gap-2 rounded-md border border-white/20 px-6 py-3 font-display text-xs font-extrabold uppercase text-white transition-colors hover:border-neon-cyan hover:text-neon-cyan">
          Back To Home
        </Link>
      </div>
    </div>
  );
}
